import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { makeAutoObservable, observable, ObservableMap, values } from 'mobx'
import { CreateTodoInput, Todo, TodoClient, UpdateTodoInput } from 'api/clients/TodoClient'
import { CollabItemType } from './DocStore.types'
import { defaultMobxCacheMaxAge, type RootStore } from 'stores/RootStore'
import { CommentableType } from 'components/ActivityEvent/Types'
import { QueryClient } from 'react-query'
import { Filters } from 'screens/Todos/hooks/useFilters'
import { makePersistable } from 'mobx-persist-store'

export class TodosStore {
  rootStore: RootStore
  todos: ObservableMap<number, Todo> = observable.map()
  highlighted?: Todo = undefined
  selected?: Todo = undefined

  private _isLoading: boolean = false
  private _isUpdating: boolean = false

  constructor(rootStore: RootStore, shouldPersist = true) {
    makeAutoObservable(this)
    this.rootStore = rootStore

    if (shouldPersist) {
      makePersistable(this, {
        name: 'TodosStore',
        properties: ['todos'],
        storage: window.localStorage,
        stringify: false,
        expireIn: defaultMobxCacheMaxAge,
      })
    }
  }

  select = (id?: number) => {
    if (id === undefined) {
      this.selected = undefined
    } else {
      this.selected = this.todos.get(id)
      this.markAsRead(id)
      if (this.highlighted?.id === id) {
        this.highlight(undefined)
      }
    }
  }

  highlight = (id?: number) => {
    if (id === undefined) {
      this.highlighted = undefined
    } else {
      this.highlighted = this.todos.get(id)
    }
  }

  private setLoading = (loading: boolean) => {
    this._isLoading = loading
  }

  private setUpdating = (updating: boolean) => {
    this._isUpdating = updating
  }

  get isLoading() {
    return this._isLoading
  }

  get isUpdating() {
    return this._isUpdating
  }

  /**
   * Todo CRUD
   */

  add = (todo: Todo) => {
    this.todos.set(todo.id, todo)
    todo.comments?.forEach((comment) => this.rootStore.comments.add(comment))
  }

  bulkAdd = (todos: Todo[]) => {
    todos.forEach((todo) => this.add(todo))
  }

  get = (id: number) => {
    return this.todos.get(id)
  }

  getByUuid = (uuid: string) => {
    return this.all.find((todo) => todo.uuid === uuid)
  }

  get all() {
    return values(this.todos)
  }

  get incomplete() {
    return this.all.filter((todo) => !todo.completed_at)
  }

  getByArticle = (id: string) => {
    return this.all.filter((todo) => todo.article_id === id)
  }

  remove = (id: number) => {
    this.todos.delete(id)
  }

  updateAttrs = (id: number, attributes: Partial<Todo>) => {
    const todo = this.get(id)
    if (!todo) return
    Object.assign(todo, attributes)
  }

  /**
   * Async operations
   */

  load = async (articleId: string) => {
    this.setLoading(true)
    const response = await TodoClient.getTodos(articleId)
    if (response.type === SUCCESS_RESPONSE_TYPE && response.data.todos) {
      this.bulkAdd(response.data.todos)
    }
    this.setLoading(false)
  }

  delete = async (id: number, options?: { trackArticleUpdate: boolean }) => {
    const response = await TodoClient.deleteTodo(id, options?.trackArticleUpdate)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error(response.data.message)
    }
    this.remove(id)
  }

  create = async (articleId: string, payload: CreateTodoInput, attachedToContent: boolean = false) => {
    const response = await TodoClient.createTodo(articleId, payload, attachedToContent)
    if (response.type === SUCCESS_RESPONSE_TYPE) {
      this.add(response.data.todo)

      if (this.rootStore.docStore?.inCleanupMode) {
        this.rootStore.docStore.cleanupModeStore.markForCleaning(CollabItemType.Todo)
      }
      return response.data.todo
    } else {
      throw new Error(response.data.message)
    }
  }

  update = async (id: number, payload: UpdateTodoInput) => {
    if (this.isUpdating) return

    const todo = this.get(id)
    if (!todo) return

    this.setUpdating(true)

    /**
     * Optimistic update
     */
    const oldAttrs = { ...todo }
    this.updateAttrs(todo.id, payload)

    const response = await TodoClient.updateTodo(id, payload)
    this.setUpdating(false)

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      this.updateAttrs(todo.id, response.data.todo)
    } else {
      this.updateAttrs(todo.id, oldAttrs)
      throw new Error(response.data.message)
    }
  }

  toggleCompleted = async (id: number, queryClient?: QueryClient, filters?: Filters) => {
    const todo = this.get(id)
    if (!todo) return
    const { completed_at } = todo
    this.updateAttrs(id, { completed_at: completed_at ? null : new Date().toISOString() })

    if (this.rootStore.docStore?.inCleanupMode && !completed_at) {
      this.rootStore.docStore?.cleanupModeStore?.clean(CollabItemType.Todo)
    }

    const response = !completed_at ? await TodoClient.completeTodo(id) : await TodoClient.undoTodo(id)

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      this.updateAttrs(id, { completed_at })
      throw new Error(response.data.message)
    } else {
      queryClient?.invalidateQueries(['articles', todo.article_id, 'tasks', filters])
    }
  }

  batchComplete = async (todos: Todo[]) => {
    if (todos.length < 1) return

    todos.forEach((todo) => this.updateAttrs(todo.id, { completed_at: new Date().toISOString() }))
    const response = await TodoClient.batchCompleteTodos(todos.map((todo) => todo.id))

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      todos.forEach((todo) => this.updateAttrs(todo.id, { completed_at: null }))

      throw new Error(response.data)
    }
  }

  markAsRead = (id: number) => {
    const todo = this.get(id)
    if (!todo) return
    const comments = this.rootStore.comments.getByCommentable(id, CommentableType.TODO)
    comments.forEach((comment) => this.rootStore.comments.markAsRead(comment.id))
  }

  toggleReaction = async (id: number, reaction: string) => {
    const todo = this.get(id)
    if (!todo) return

    const response = await TodoClient.toggleReaction(id, reaction)
    if (response.type === SUCCESS_RESPONSE_TYPE) {
      this.updateAttrs(id, response.data.todo)
    } else {
      throw new Error(response.data.message)
    }
  }
}
